/**
 * <EvalsStrip> — Agent evaluation metrics widget (FR-009).
 *
 * Renders three KPI tiles for the latest evaluation rollup:
 *   - Goal Success Rate
 *   - Tool Accuracy
 *   - Failure Coverage
 *
 * Colour-coded status using Cohere 2026 tokens only (no inline hex/rgb):
 *   - success (green) when above the warning threshold
 *   - warning (amber) when between danger and warning thresholds
 *   - danger (red) when below the danger threshold
 *
 * @see specs/prd.md FR-009, Section 7.1
 * @see shared/types/evals.ts
 */
import React from "react";
import type { EvalRollup, EvalStatus } from "../../../../../shared/types/evals";
import { DEFAULT_EVAL_THRESHOLDS } from "../../../../../shared/types/evals";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface EvalsStripProps {
  /** The rollup data to display. */
  rollup: EvalRollup;
}

// ---------------------------------------------------------------------------
// Status classification helpers
// ---------------------------------------------------------------------------

function classifyGoalSuccess(rate: number): EvalStatus {
  if (rate < DEFAULT_EVAL_THRESHOLDS.goalSuccessDanger) return "danger";
  if (rate < DEFAULT_EVAL_THRESHOLDS.goalSuccessWarning) return "warning";
  return "success";
}

function classifyToolAccuracy(rate: number): EvalStatus {
  if (rate < DEFAULT_EVAL_THRESHOLDS.toolAccuracyDanger) return "danger";
  if (rate < DEFAULT_EVAL_THRESHOLDS.toolAccuracyWarning) return "warning";
  return "success";
}

function classifyFailureCoverage(rate: number): EvalStatus {
  if (rate < DEFAULT_EVAL_THRESHOLDS.failureCoverageDanger) return "danger";
  if (rate < DEFAULT_EVAL_THRESHOLDS.failureCoverageWarning) return "warning";
  return "success";
}

// ---------------------------------------------------------------------------
// Tile sub-component
// ---------------------------------------------------------------------------

interface EvalTileProps {
  label: string;
  rate: number;
  status: EvalStatus;
}

function EvalTile({ label, rate, status }: EvalTileProps) {
  return (
    <div
      className="evals-strip__tile"
      data-eval-status={status}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "var(--space-3, 12px)",
        borderRadius: "var(--r-md, 8px)",
        backgroundColor: "var(--color-surface-secondary)",
        flex: "1 1 0",
        minWidth: "0",
      }}
    >
      <span
        className="evals-strip__label"
        style={{
          fontSize: "var(--text-sm, 14px)",
          color: "var(--color-text-secondary)",
          marginBottom: "var(--space-1, 4px)",
        }}
      >
        {label}
      </span>
      <span
        className="evals-strip__value"
        style={{
          fontSize: "var(--text-xl, 20px)",
          fontWeight: "var(--font-weight-semibold, 600)" as unknown as number,
          color:
            status === "success"
              ? "var(--color-emerald)"
              : status === "warning"
                ? "var(--color-amber)"
                : "var(--color-coral)",
        }}
      >
        {(rate * 100).toFixed(1)}%
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EvalsStrip
// ---------------------------------------------------------------------------

export default function EvalsStrip({ rollup }: EvalsStripProps) {
  const goalStatus = classifyGoalSuccess(rollup.goalSuccessRate);
  const toolStatus = classifyToolAccuracy(rollup.toolAccuracyRate);
  const coverageStatus = classifyFailureCoverage(rollup.failureCoverage);

  return (
    <section
      className="evals-strip"
      aria-label="Agent evaluation metrics"
      style={{
        display: "flex",
        gap: "var(--space-3, 12px)",
        marginTop: "var(--space-4, 16px)",
      }}
    >
      <EvalTile
        label="Goal Success"
        rate={rollup.goalSuccessRate}
        status={goalStatus}
      />
      <EvalTile
        label="Tool Accuracy"
        rate={rollup.toolAccuracyRate}
        status={toolStatus}
      />
      <EvalTile
        label="Failure Coverage"
        rate={rollup.failureCoverage}
        status={coverageStatus}
      />
    </section>
  );
}
